import { RUNTIME_BACKEND_URL } from '../../lib/backendUrl';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Users,
  Calendar,
  Package,
  TrendingUp,
  Plus,
  ArrowRight,
  CalendarDays,
  Loader2
} from 'lucide-react';

const API = RUNTIME_BACKEND_URL;

export default function SalonDashboardPage() {
  const { token, user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCustomers: 0,
    upcomingEvents: 0,
    totalPackages: 0,
    totalRevenue: 0
  });
  const [upcomingCustomers, setUpcomingCustomers] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Müşterileri yükle
      const customersRes = await fetch(`${API}/api/salon/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const customers = customersRes.ok ? await customersRes.json() : [];
      
      // Paketleri yükle
      const packagesRes = await fetch(`${API}/api/salon/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const packages = packagesRes.ok ? await packagesRes.json() : [];
      
      // İstatistikleri hesapla
      const now = new Date();
      const upcoming = customers.filter(c => new Date(c.event_date) >= now);
      const totalRevenue = customers.reduce((sum, c) => sum + (c.total_price || 0), 0);
      
      setStats({
        totalCustomers: customers.length,
        upcomingEvents: upcoming.length,
        totalPackages: packages.length,
        totalRevenue
      });
      
      // Yaklaşan etkinlikler (önümüzdeki 30 gün)
      const thirtyDaysLater = new Date();
      thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
      const upcomingFiltered = upcoming
        .filter(c => new Date(c.event_date) <= thirtyDaysLater)
        .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
        .slice(0, 5);
      setUpcomingCustomers(upcomingFiltered);
      
    } catch (error) {
      console.error('Dashboard load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="salon-dashboard">
      {/* Welcome */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">
            Hoş Geldiniz, {user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground">{user?.company_name}</p>
        </div>
        <Link to="/salon/musteriler/yeni">
          <Button data-testid="new-customer-btn">
            <Plus className="h-4 w-4 mr-2" />
            Yeni Rezervasyon
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="animate-fade-in">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Toplam Müşteri
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCustomers}</div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in stagger-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Yaklaşan Etkinlik
            </CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.upcomingEvents}</div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in stagger-2">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Paket Sayısı
            </CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalPackages}</div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in stagger-3">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Toplam Gelir
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalRevenue)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Events */}
      <Card className="animate-fade-in stagger-4">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" />
            Yaklaşan Etkinlikler
          </CardTitle>
          <Link to="/salon/musteriler">
            <Button variant="ghost" size="sm">
              Tümünü Gör
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {upcomingCustomers.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Yaklaşan etkinlik bulunmuyor.
            </p>
          ) : (
            <div className="space-y-4">
              {upcomingCustomers.map((customer) => (
                <Link 
                  key={customer.id}
                  to={`/salon/musteriler/${customer.id}`}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {customer.bride_name} & {customer.groom_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(customer.event_date)} • {customer.guest_count || '-'} Kişi
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-primary">
                      {formatCurrency(customer.total_price || 0)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
